class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.effectiveQueries = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  async onFormSubmit(event) {
    const searchTerm = this.getQuery();
    if (!searchTerm.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    const relatedQuery = this.getRelatedPartQuery(searchTerm);
    if (!relatedQuery) return;
    event.preventDefault();
    if (this.pendingSearchSubmit) return;
    this.pendingSearchSubmit = true;
    const form = event.currentTarget;
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const key = JSON.stringify([searchTerm, filterType, filterTypeQuery]);
    let effectiveQuery = this.effectiveQueries[key];
    try {
      if (!effectiveQuery) {
        // Resolve independently of the debounced dropdown request.
        const signal = new AbortController().signal;
        const productSelector = '[id^="predictive-search-option-product-"] a';
        const original = await this.fetchSearchSection(searchTerm, filterType, filterTypeQuery, signal);
        effectiveQuery = searchTerm;
        if (!original.querySelector(productSelector)) {
          const related = await this.fetchSearchSection(relatedQuery, filterType, filterTypeQuery, signal);
          if (related.querySelector(productSelector)) effectiveQuery = relatedQuery;
        }
      }
    } catch (error) {
      console.warn("Search fallback unavailable", error);
      effectiveQuery = searchTerm;
    } finally {
      this.pendingSearchSubmit = false;
    }
    // Never submit an old query if the customer edited it while we waited.
    if (this.getQuery() !== searchTerm || this.getSearchFilterType() !== filterType ||
        this.getSearchFilterTypeQuery() !== filterTypeQuery) return;
    // Navigate once without changing the visible input or firing submit again.
    const destination = new URL(form.action, window.location.href);
    const parameters = new URLSearchParams(new FormData(form));
    parameters.set("q", effectiveQuery || searchTerm);
    destination.search = parameters.toString();
    window.location.assign(destination.toString());
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search-item-button"),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  // Only broaden an incomplete Yamaha-style suffix, never a complete part number.
  getRelatedPartQuery(searchTerm) {
    const match = /^([a-z0-9]{3}-[a-z0-9]{5})-[a-z0-9]?$/i.exec(searchTerm);
    return match ? match[1] : null;
  }

  async fetchSearchSection(searchTerm, filterType, filterTypeQuery, signal) {
    const query = filterTypeQuery
      ? `${filterType}:${filterTypeQuery}+* ${searchTerm}`
      : searchTerm;
    const url = new URL(routes.predictive_search_url, window.location.origin);
    url.searchParams.set("q", query);
    url.searchParams.set("resources[limit_scope]", this.dataset.limitScope || "all");
    url.searchParams.set("resources[limit]", this.dataset.limit || "10");
    url.searchParams.set("section_id", "predictive-search");
    url.searchParams.set(
      "resources[options][fields]",
      "title,product_type,variants.title,variants.sku,vendor",
    );
    const response = await fetch(url.toString(), { signal });
    if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
    const html = await response.text();
    const section = new DOMParser().parseFromString(html, "text/html")
      .querySelector("#shopify-section-predictive-search");
    if (!section) throw new Error("Predictive search section missing");
    return section;
  }

  async getSearchResults(searchTerm) {
    this.searchController?.abort();
    const controller = new AbortController();
    this.searchController = controller;
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    const queryKey = JSON.stringify([searchTerm, filterType, filterTypeQuery]);
    const isCurrent = () =>
      !controller.signal.aborted &&
      this.searchController === controller &&
      this.getQuery() === searchTerm &&
      this.getSearchFilterType() === filterType &&
      this.getSearchFilterTypeQuery() === filterTypeQuery;

    this.setLiveRegionLoadingState();

    try {
      let markup = this.cachedResults[queryKey];
      if (!markup) {
        let effectiveQuery = searchTerm;
        let section = await this.fetchSearchSection(
          searchTerm, filterType, filterTypeQuery, controller.signal,
        );
        if (!isCurrent()) return;
        // This selector is present in the store's current predictive-search section.
        const productSelector = '[id^="predictive-search-option-product-"] a';
        const relatedQuery = this.getRelatedPartQuery(searchTerm);
        if (relatedQuery && !section.querySelector(productSelector)) {
          try {
            const related = await this.fetchSearchSection(
              relatedQuery, filterType, filterTypeQuery, controller.signal,
            );
            if (!isCurrent()) return;
            const results = related.querySelector("#predictive-search-results");
            const searchButton = related.querySelector("button.predictive-search-item-button");
            if (results && searchButton && related.querySelector(productSelector)) {
              // Preserve the typed query in the label without removing the arrow.
              const updateSearchLabel = (node) => {
                if (node.nodeType === 3) {
                  node.nodeValue = node.nodeValue.split(relatedQuery).join(searchTerm);
                } else {
                  node.childNodes.forEach(updateSearchLabel);
                }
              };
              updateSearchLabel(searchButton);
              effectiveQuery = relatedQuery;
              section = related;
            }
          } catch (error) {
            if (error.name === "AbortError") throw error;
            // Keep the successful original response if the optional fallback fails.
            console.warn("Related part search unavailable", error);
          }
        }
        markup = section.innerHTML;
        if (isCurrent()) {
          this.cachedResults[queryKey] = markup;
          this.effectiveQueries[queryKey] = effectiveQuery;
        }
      }
      if (isCurrent() && this.contains(document.activeElement)) {
        this.renderSearchResults(markup);
      }
    } catch (error) {
      if (error.name !== "AbortError" && isCurrent()) {
        this.close();
        console.error("Predictive search unavailable", error);
      }
    } finally {
      if (this.searchController === controller) this.removeAttribute("loading");
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]").textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    const top = this.querySelector(".predictive-search").getBoundingClientRect().top;
    this.predictiveSearchResults.style.maxHeight = `calc(100dvh - ${top}px - 30px)`;

    if (!this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsOpen);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.searchController?.abort();
    this.removeAttribute("loading");
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      publish(PUB_SUB_EVENTS.searchResultsClose);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
